<template>
  <div class="tab-content">
    <div v-if="!group">
      <p class="text-center mt-5">
        {{ $t('adminAccountManagement.selectAccount') }}
      </p>
    </div>
    <div v-else>
      <!-- Disabled / trial banners -->
      <b-row v-if="isDisabled || (isTestAccount && (trialExpired || (trialDaysRemaining !== null && trialDaysRemaining <= 7)))" class="mb-3">
        <b-col cols="12" offset-sm="1" sm="11">
          <b-alert
            v-if="isDisabled"
            show
            variant="danger"
            class="mb-0"
          >
            <b-icon icon="slash-circle" class="mr-2" aria-hidden="true"></b-icon>
            <strong>{{ $t('adminAccountManagement.banners.disabledTitle') }}</strong>
            — {{ $t('adminAccountManagement.banners.disabledText') }}
          </b-alert>
          <b-alert
            v-else-if="isTestAccount && trialExpired"
            show
            variant="danger"
            class="mb-0"
          >
            <b-icon icon="hourglass-bottom" class="mr-2" aria-hidden="true"></b-icon>
            <strong>{{ $t('adminAccountManagement.banners.trialExpiredTitle') }}</strong>
            — {{ $t('adminAccountManagement.banners.trialExpiredText') }}
          </b-alert>
          <b-alert
            v-else-if="isTestAccount && trialDaysRemaining !== null && trialDaysRemaining <= 7"
            show
            variant="warning"
            class="mb-0"
          >
            <b-icon icon="hourglass-split" class="mr-2" aria-hidden="true"></b-icon>
            <strong>{{ $t('adminAccountManagement.banners.trialEndingTitle') }}</strong>
            — {{ $t('adminAccountManagement.banners.trialEndingText', { days: trialDaysRemaining }) }}
          </b-alert>
        </b-col>
      </b-row>

      <!-- Header -->
      <b-row class="mb-2">
        <b-col cols="12" offset-sm="1" sm="11" class="d-flex align-items-center flex-wrap">
          <h3 class="mb-0">{{ group.name }}</h3>
          <b-badge v-if="group.slug" variant="secondary" class="ml-3">
            {{ group.slug }}
          </b-badge>
          <span v-if="isTestAccount" class="header-flag flag-warn ml-2">
            {{ $t('adminAccountManagement.flag.testAccount') }}
          </span>
          <span v-else class="header-flag flag-success ml-2">
            {{ $t('adminAccountManagement.flag.paidAccount') }}
          </span>
          <span v-if="isVue3Editor" class="header-flag flag-info ml-2">
            {{ $t('adminAccountManagement.flag.vue3Editor') }}
          </span>
          <span v-if="hasCustomFonts" class="header-flag flag-info ml-2">
            {{ $t('adminAccountManagement.flag.customFonts') }}
          </span>
          <span v-if="hasPersonalization" class="header-flag flag-info ml-2">
            {{ $t('adminAccountManagement.flag.personalization') }}
          </span>
        </b-col>
        <b-col v-if="group.domain" cols="12" offset-sm="1" sm="11">
          <small class="text-muted">{{ group.domain }}</small>
        </b-col>
        <b-col cols="12" offset-sm="1" sm="11">
          <hr>
        </b-col>
      </b-row>

      <!-- Stats grid -->
      <b-row class="mb-2">
        <b-col cols="12" offset-sm="1" sm="11" class="d-flex align-items-center">
          <h3 class="mb-0 text-uppercase">
            {{ $t('adminAccountManagement.statistics') }}
          </h3>
        </b-col>
      </b-row>
      <b-row class="mb-5">
        <b-col cols="12" offset-sm="1" sm="11">
          <div class="stats-grid">
            <div
              v-for="stat in statsList"
              :key="stat.key"
              :class="['stat-card', stat.tone ? `tone-${stat.tone}` : '']"
              v-b-tooltip.hover
              :title="stat.tooltip || ''"
            >
              <div class="stat-icon">
                <b-icon :icon="stat.icon" aria-hidden="true" scale="1.2"></b-icon>
              </div>
              <div class="stat-body">
                <div class="stat-value">{{ stat.value }}</div>
                <div class="stat-label">{{ stat.label }}</div>
                <div v-if="stat.sub" class="stat-sub">{{ stat.sub }}</div>
                <div v-if="stat.key === 'images'" class="stat-sub">
                  <span v-if="storageBytes !== null">
                    {{ formattedBytes(storageBytes) }}
                  </span>
                  <span v-else-if="storageLoading" class="text-muted">…</span>
                  <span v-else-if="storageError" class="text-danger">
                    {{ $t('adminAccountManagement.stats.calculateStorageError') }}
                  </span>
                  <a
                    v-else
                    href="#"
                    class="stat-action-link"
                    @click.prevent.stop="loadStorageBytes"
                  >
                    {{ $t('adminAccountManagement.stats.calculateStorage') }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Analytics -->
      <b-row class="mb-2">
        <b-col cols="12" offset-sm="1" sm="11" class="d-flex align-items-center">
          <h3 class="mb-0 text-uppercase">
            {{ $t('adminAccountManagement.analytics.title') }}
          </h3>
          <b-button
            v-if="pirschCode"
            variant="link"
            size="sm"
            class="ml-2 p-0"
            @click="analyticsOpen = !analyticsOpen"
          >
            {{ analyticsOpen
              ? $t('adminAccountManagement.analytics.hide')
              : $t('adminAccountManagement.analytics.show') }}
          </b-button>
        </b-col>
      </b-row>
      <b-row class="mb-5">
        <b-col cols="12" offset-sm="1" sm="11">
          <p v-if="!pirschCode" class="text-muted">
            {{ $t('adminAccountManagement.analytics.notConfigured') }}
          </p>
          <b-collapse v-else v-model="analyticsOpen">
            <admin-account-analytics
              v-if="analyticsOpen"
              :pirsch-code="pirschCode"
            />
          </b-collapse>
        </b-col>
      </b-row>

      <!-- Recently published projects -->
      <b-row v-if="recentProjects.length" class="mb-2">
        <b-col cols="12" offset-sm="1" sm="11" class="d-flex align-items-center">
          <h3 class="mb-0 text-uppercase">
            {{ $t('adminAccountManagement.recentProjects.title') }}
          </h3>
          <small class="text-muted ml-2">
            {{ $t('adminAccountManagement.recentProjects.subtitle', { count: recentProjects.length }) }}
          </small>
        </b-col>
      </b-row>
      <b-row v-if="recentProjects.length" class="mb-5">
        <b-col cols="12" offset-sm="1" sm="11">
          <b-table
            :fields="recentProjectFields"
            :items="recentProjects"
            borderless
            dark
            class="table-view recent-projects-table"
          >
            <template #table-colgroup="scope">
              <col
                v-for="field in scope.fields"
                :key="field.key"
                :style="{ width: field.width }"
              >
            </template>
            <template #cell(name)="data">
              <span class="project-name">{{ data.item.name || '—' }}</span>
              <small v-if="data.item.slug" class="text-muted ml-2">/{{ data.item.slug }}</small>
            </template>
            <template #cell(published_at)="data">
              <span
                v-if="data.item.published_at"
                v-b-tooltip.hover
                :title="formatDateTime(data.item.published_at)"
              >
                {{ formatRelative(data.item.published_at) }}
              </span>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(updated_at)="data">
              <span
                v-if="data.item.updated_at"
                v-b-tooltip.hover
                :title="formatDateTime(data.item.updated_at)"
              >
                {{ formatRelative(data.item.updated_at) }}
              </span>
              <span v-else class="text-muted">—</span>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <!-- Users -->
      <b-row class="mb-2">
        <b-col cols="12" offset-sm="1" sm="3" class="d-flex align-items-center">
          <h3 class="mb-0 text-uppercase">
            {{ $t('adminAccountManagement.accountUsers') }}
          </h3>
        </b-col>
        <b-col cols="12" sm="8" class="align-content-end">
          <div class="float-right d-flex">
            <b-input-group class="search-group d-flex align-items-center">
              <b-icon icon="search" aria-hidden="true" scale="1"></b-icon>
              <b-form-input
                id="filter-input-admin-users"
                v-model="userFilter"
                type="search"
                :placeholder="$t('webmag.search')"
              ></b-form-input>
            </b-input-group>
          </div>
        </b-col>
      </b-row>
      <b-row class="mb-5">
        <b-col cols="12" offset-sm="1" sm="11">
          <b-table
            :fields="userFields"
            :items="users"
            :filter="userFilter"
            sort-icon-left
            show-empty
            borderless
            dark
            class="table-view users-table"
          >
            <template #empty>
              <div class="text-center my-2">
                <b-icon icon="exclamation-circle-fill" class="mr-2" aria-hidden="true" scale="1"></b-icon>
                {{ $t('table.noUsers') }}
              </div>
            </template>
            <template #emptyfiltered>
              <div class="text-center my-2">
                <b-icon icon="exclamation-circle-fill" class="mr-2" aria-hidden="true" scale="1"></b-icon>
                {{ $t('table.noFilteredUsers') }}
              </div>
            </template>
            <template #table-colgroup="scope">
              <col
                v-for="field in scope.fields"
                :key="field.key"
                :style="{ width: field.width }"
              >
            </template>
            <template #cell(image)="data">
              <b-avatar
                v-if="data.item.image"
                size="32px"
                :src="data.item.image"
              ></b-avatar>
              <b-avatar v-else size="32px"></b-avatar>
            </template>
            <template #cell(name)="data">
              {{ data.item.name || '—' }}
            </template>
            <template #cell(email)="data">
              {{ data.item.email }}
            </template>
            <template #cell(role)="data">
              {{ data.item.role.name }}
            </template>
            <template #cell(via)="data">
              <span v-if="data.item.viaAccount" class="via-pill via-account">
                {{ $t('adminAccountManagement.via.account') }}
              </span>
              <template v-if="data.item.teams && data.item.teams.length">
                <span
                  v-for="teamName in data.item.teams"
                  :key="teamName"
                  class="via-pill via-team ml-1"
                >
                  {{ teamName }}
                </span>
              </template>
            </template>
            <template #cell(status)="data">
              <span :class="['status-pill', `status-${data.item.statusKey}`]">
                {{ data.item.status }}
              </span>
            </template>
            <template #cell(lastLogin)="data">
              <span v-if="data.item.last_login" v-b-tooltip.hover :title="formatDateTime(data.item.last_login)">
                {{ formatRelative(data.item.last_login) }}
              </span>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(loginCount)="data">
              {{ data.item.login_count || 0 }}
            </template>
          </b-table>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import GetAdminAccountDetails from '@/graphQlQueries/queries/getAdminAccountDetails';
import GetAdminAccountImageStorage from '@/graphQlQueries/queries/getAdminAccountImageStorage';
import AdminAccountAnalytics from '@/components/settings/admin/AdminAccountAnalytics.vue';
import { formatDateTime, formatRelative } from './dateFormatters';

const ROLE_SORT_ORDER = {
  accountManager: 0,
  administrator: 1,
  manager: 2,
  editor: 3,
  user: 4,
};

const TRIAL_DAYS = 14;
const DAY = 24 * 60 * 60 * 1000;

function pickImageBytes(metadata) {
  if (!metadata || typeof metadata !== 'object') return 0;
  // Support a few likely shapes; fall back to 0.
  const candidate = metadata.size
    ?? metadata.fileSize
    ?? metadata.bytes
    ?? metadata.file_size
    ?? metadata?.file?.size;
  const num = Number(candidate);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

function formatBytes(bytes) {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default {
  name: 'AdminAccountDetails',
  components: { AdminAccountAnalytics },
  props: ['group'],
  data() {
    return {
      details: null,
      analyticsOpen: false,
      userFilter: null,
      storageBytes: null,
      storageLoading: false,
      storageError: false,
      userFields: [
        {
          key: 'image', label: '', sortable: false, width: '50px', tdClass: 'image_column',
        },
        {
          key: 'name',
          label: this.$t('adminAccountManagement.table.name'),
          sortable: true,
          width: '160px',
          tdClass: 'name_column',
        },
        {
          key: 'email',
          label: this.$t('adminAccountManagement.table.email'),
          sortable: true,
          width: 'auto',
          tdClass: 'email_column',
        },
        {
          key: 'role',
          label: this.$t('adminAccountManagement.table.role'),
          sortable: true,
          width: '120px',
        },
        {
          key: 'via',
          label: this.$t('adminAccountManagement.table.via'),
          sortable: true,
          width: '160px',
        },
        {
          key: 'status',
          label: this.$t('adminAccountManagement.table.status'),
          sortable: true,
          width: '110px',
        },
        {
          key: 'lastLogin',
          label: this.$t('adminAccountManagement.table.lastLogin'),
          sortable: true,
          sortByFormatted: false,
          width: '140px',
          formatter: (value, key, item) => (item.last_login ? new Date(item.last_login).getTime() : 0),
        },
        {
          key: 'loginCount',
          label: this.$t('adminAccountManagement.table.loginCount'),
          sortable: true,
          width: '90px',
        },
      ],
      recentProjectFields: [
        {
          key: 'name',
          label: this.$t('adminAccountManagement.recentProjects.name'),
          tdClass: 'project_name_column',
        },
        {
          key: 'published_at',
          label: this.$t('adminAccountManagement.recentProjects.publishedAt'),
          width: '180px',
        },
        {
          key: 'updated_at',
          label: this.$t('adminAccountManagement.recentProjects.updatedAt'),
          width: '180px',
        },
      ],
    };
  },
  apollo: {
    details: {
      query: GetAdminAccountDetails,
      variables() {
        return {
          gid: this.group.group_id,
          thirtyDaysAgo: new Date(Date.now() - 30 * DAY).toISOString(),
        };
      },
      update(data) {
        return data;
      },
      skip() {
        return !this.group || !this.group.group_id;
      },
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    settings() {
      return this.details?.groups_by_pk?.settings || this.group?.settings || {};
    },
    pirschCode() {
      return this.details?.groups_by_pk?.pirsch_code || null;
    },
    recentProjects() {
      return this.details?.recentlyPublishedProjects || [];
    },
    isTestAccount() {
      return this.settings.isTestAccount === true;
    },
    isDisabled() {
      return this.settings.disableAccount === true;
    },
    isVue3Editor() {
      return this.settings.enable_vue3_editor === true;
    },
    hasCustomFonts() {
      return this.settings.enableCustomFonts === true;
    },
    hasPersonalization() {
      return this.settings.usePersonalization === true;
    },
    createdAt() {
      return this.details?.groups_by_pk?.created_at || null;
    },
    accountAgeDays() {
      if (!this.createdAt) return null;
      return Math.max(0, Math.floor((Date.now() - new Date(this.createdAt).getTime()) / DAY));
    },
    trialDaysRemaining() {
      if (!this.isTestAccount || !this.createdAt) return null;
      const expires = new Date(this.createdAt).getTime() + TRIAL_DAYS * DAY;
      return Math.ceil((expires - Date.now()) / DAY);
    },
    trialExpired() {
      return this.trialDaysRemaining !== null && this.trialDaysRemaining <= 0;
    },
    lastActivityAt() {
      const max = this.details?.pagesTotal?.aggregate?.max;
      if (!max) return null;
      const u = max.updated_at ? new Date(max.updated_at).getTime() : 0;
      const p = max.published_at ? new Date(max.published_at).getTime() : 0;
      const ts = Math.max(u, p);
      return ts > 0 ? new Date(ts).toISOString() : null;
    },
    activeUsersLast30Count() {
      if (!this.users?.length) return 0;
      const cutoff = Date.now() - 30 * DAY;
      return this.users.filter((u) => u.last_login && new Date(u.last_login).getTime() >= cutoff).length;
    },
    neverLoggedInCount() {
      if (!this.users?.length) return 0;
      return this.users.filter((u) => !u.last_login).length;
    },
    statsList() {
      const aggCount = (key) => {
        if (!this.details || !this.details[key]) return 0;
        return this.details[key].aggregate?.count ?? 0;
      };
      const list = [
        {
          key: 'pages',
          icon: 'file-earmark-text',
          value: aggCount('pagesTotal'),
          label: this.$t('adminAccountManagement.stats.pages'),
        },
        {
          key: 'projects',
          icon: 'collection',
          value: aggCount('projectsTotal'),
          label: this.$t('adminAccountManagement.stats.projects'),
        },
        {
          key: 'folders',
          icon: 'folder',
          value: aggCount('foldersTotal'),
          label: this.$t('adminAccountManagement.stats.folders'),
        },
        {
          key: 'pagesPublished',
          icon: 'cloud-check',
          value: aggCount('pagesPublished'),
          label: this.$t('adminAccountManagement.stats.publishedPages'),
        },
        {
          key: 'projectsPublished',
          icon: 'cloud-check',
          value: aggCount('projectsPublished'),
          label: this.$t('adminAccountManagement.stats.publishedProjects'),
        },
        {
          key: 'foldersPublished',
          icon: 'cloud-check',
          value: aggCount('foldersPublished'),
          label: this.$t('adminAccountManagement.stats.publishedFolders'),
        },
        {
          key: 'pagesLast30',
          icon: 'graph-up',
          value: aggCount('pagesPublishedLast30'),
          label: this.$t('adminAccountManagement.stats.pagesPublishedLast30'),
        },
        {
          key: 'lastActivity',
          icon: 'clock-history',
          value: this.lastActivityAt
            ? this.formatRelative(this.lastActivityAt)
            : this.$t('adminAccountManagement.stats.never'),
          tooltip: this.lastActivityAt ? this.formatDateTime(this.lastActivityAt) : '',
          label: this.$t('adminAccountManagement.stats.lastActivity'),
          tone: this.lastActivityAt
            && (Date.now() - new Date(this.lastActivityAt).getTime()) > 90 * DAY
            ? 'warn' : null,
        },
        {
          key: 'accountAge',
          icon: 'calendar-event',
          value: this.accountAgeDays !== null ? this.$tc('adminAccountManagement.stats.daysValue', this.accountAgeDays, { days: this.accountAgeDays }) : '—',
          tooltip: this.createdAt ? this.formatDateTime(this.createdAt) : '',
          label: this.$t('adminAccountManagement.stats.accountAge'),
        },
        {
          key: 'images',
          icon: 'images',
          value: aggCount('imagesTotal'),
          label: this.$t('adminAccountManagement.stats.mediaImages'),
        },
        {
          key: 'activeUsers',
          icon: 'person-check',
          value: `${this.activeUsersLast30Count} / ${this.users.length}`,
          label: this.$t('adminAccountManagement.stats.activeUsersLast30'),
        },
        {
          key: 'neverLoggedIn',
          icon: 'person-x',
          value: this.neverLoggedInCount,
          label: this.$t('adminAccountManagement.stats.neverLoggedIn'),
          tone: this.neverLoggedInCount > 0 ? 'warn' : null,
        },
      ];
      if (this.isTestAccount) {
        let trialTone = null;
        if (this.trialExpired) {
          trialTone = 'danger';
        } else if (this.trialDaysRemaining !== null && this.trialDaysRemaining <= 7) {
          trialTone = 'warn';
        }
        list.push({
          key: 'trial',
          icon: 'hourglass-split',
          value: this.trialDaysRemaining !== null
            ? this.$tc('adminAccountManagement.stats.daysValue', this.trialDaysRemaining, { days: this.trialDaysRemaining })
            : '—',
          label: this.$t('adminAccountManagement.stats.trialDaysRemaining'),
          tone: trialTone,
        });
      }
      return list;
    },
    users() {
      if (!this.details) return [];
      const byUserId = new Map();

      const buildEntry = (u, role) => {
        // eslint-disable-next-line no-nested-ternary
        const statusKey = u.deactivated
          ? 'deactivated'
          : (u.email_confirmed ? 'online' : 'invited');
        return {
          userId: u.user_id,
          name: u.full_name,
          email: u.email,
          deactivated: u.deactivated,
          active: u.active,
          email_confirmed: u.email_confirmed,
          last_login: u.last_login,
          login_count: u.login_count,
          created_at: u.created_at,
          image: u.profile_pictures ? this.getImageUrl(u.profile_pictures) : null,
          role: {
            role_id: role.role_id ?? null,
            handle: role.handle ?? null,
            name: role.name,
          },
          status: this.$t(`adminAccountManagement.status.${statusKey}`),
          statusKey,
          viaAccount: false,
          teams: [],
          via: '',
        };
      };

      (this.details.users_roles_groups || []).forEach((entry) => {
        const existing = byUserId.get(entry.user.user_id);
        if (existing) {
          existing.viaAccount = true;
        } else {
          const obj = buildEntry(entry.user, entry.role);
          obj.viaAccount = true;
          byUserId.set(entry.user.user_id, obj);
        }
      });

      const seenTeamPair = new Set();
      (this.details.teams_items || []).forEach((teamItem) => {
        const { team } = teamItem;
        if (!team || !team.users_roles_teams) return;
        team.users_roles_teams.forEach((entry) => {
          const { user: u } = entry;
          const pairKey = `${u.user_id}::${team.name}`;
          if (seenTeamPair.has(pairKey)) return;
          seenTeamPair.add(pairKey);

          const existing = byUserId.get(u.user_id);
          if (existing) {
            if (!existing.teams.includes(team.name)) {
              existing.teams.push(team.name);
            }
          } else {
            const obj = buildEntry(u, entry.role);
            obj.teams.push(team.name);
            byUserId.set(u.user_id, obj);
          }
        });
      });

      const userObjects = Array.from(byUserId.values()).map((u) => ({
        ...u,
        via: [
          u.viaAccount ? this.$t('adminAccountManagement.via.account') : '',
          ...u.teams,
        ].filter(Boolean).join(', '),
      }));

      userObjects.sort((a, b) => {
        if (a.viaAccount !== b.viaAccount) return a.viaAccount ? -1 : 1;
        const ra = ROLE_SORT_ORDER[a.role.handle] ?? 99;
        const rb = ROLE_SORT_ORDER[b.role.handle] ?? 99;
        if (ra !== rb) return ra - rb;
        return (a.name || a.email).localeCompare(b.name || b.email);
      });
      return userObjects;
    },
  },
  watch: {
    'group.group_id': {
      handler() {
        this.storageBytes = null;
        this.storageLoading = false;
        this.storageError = false;
      },
    },
  },
  methods: {
    formattedBytes(value) {
      return formatBytes(value);
    },
    async loadStorageBytes() {
      if (!this.group?.group_id || this.storageLoading) return;
      this.storageLoading = true;
      this.storageError = false;
      try {
        const result = await this.$apollo.query({
          query: GetAdminAccountImageStorage,
          variables: { gid: this.group.group_id },
          fetchPolicy: 'network-only',
        });
        const images = result.data?.images || [];
        this.storageBytes = images.reduce(
          (sum, img) => sum + pickImageBytes(img.metadata),
          0,
        );
      } catch (e) {
        this.storageError = true;
      } finally {
        this.storageLoading = false;
      }
    },
    getImageUrl(imageObject) {
      if (!imageObject?.url) return null;
      if (typeof this.$makeCloudImage === 'function') {
        return this.$makeCloudImage(
          imageObject.url,
          { width: 32, height: 32 },
          imageObject,
          null,
          true,
        );
      }
      return imageObject.url;
    },
    formatDateTime(value) {
      return formatDateTime(value, this.$root?.$i18n?.locale || 'en-US');
    },
    formatRelative(value) {
      return formatRelative(value, this.$root?.$i18n?.locale || 'en-US');
    },
  },
};
</script>

<style scoped lang="scss">
h3 {
  display: inline-block;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}

hr {
  border-top-color: #8E9093;
  border-top-style: solid;
}

.header-flag {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 10px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.flag-warn {
  background-color: rgba(255, 193, 7, 0.18);
  color: #ffc107;
}

.flag-success {
  background-color: rgba(83, 220, 134, 0.2);
  color: #53DC86;
}

.flag-danger {
  background-color: rgba(220, 83, 83, 0.18);
  color: #DC5353;
}

.flag-info {
  background-color: rgba(79, 70, 229, 0.22);
  color: #a3a3ff;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.stat-card {
  display: flex;
  align-items: center;
  background-color: #53575B;
  border-radius: 4px;
  padding: 14px 16px;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
}

.stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(83, 220, 134, 0.15);
  color: #53DC86;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.stat-card.tone-warn .stat-icon {
  background-color: rgba(255, 193, 7, 0.18);
  color: #ffc107;
}

.stat-card.tone-danger .stat-icon {
  background-color: rgba(220, 83, 83, 0.18);
  color: #DC5353;
}

.stat-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stat-value {
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.stat-card.tone-danger .stat-value {
  color: #ff8a8a;
}

.stat-label {
  color: #b9b9b9;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.stat-sub {
  color: #b9b9b9;
  font-size: 12px;
  margin-top: 1px;
}

.stat-action-link {
  color: #53DC86;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}

.stat-action-link:hover {
  color: #6ce89a;
}

.search-group {
  width: 150px;
  background-color: #5b6064;
  padding-left: 11px;
}

#filter-input-admin-users,
#filter-input-admin-users:focus {
  color: #b9b9b9;
  background-color: transparent;
  border: none;
  outline: none;
  box-shadow: none;
  height: calc(1.5em + 0.75rem);
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background-color: #5b6064;
  color: #d8d8d8;
}

.status-online {
  background-color: rgba(83, 220, 134, 0.2);
  color: #53DC86;
}

.status-invited {
  background-color: rgba(255, 193, 7, 0.18);
  color: #ffc107;
}

.status-deactivated {
  background-color: rgba(220, 83, 83, 0.18);
  color: #DC5353;
}

.via-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.via-account {
  background-color: rgba(83, 220, 134, 0.18);
  color: #53DC86;
}

.via-team {
  background-color: rgba(220, 83, 83, 0.18);
  color: #DC5353;
}

::v-deep table.table-view {
  font-size: 15px;
  border-collapse: separate;
  border-spacing: 0 8px;
  color: $webmag-light-grey;

  thead th {
    text-transform: uppercase;
    font-size: 13px;
    padding: 7px 8px;
  }

  tbody tr {
    background-color: #53575B;
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);

    > td {
      padding: 7px 8px;

      &:first-child {
        padding: 0;
        vertical-align: middle;
        text-align: center;
      }
    }
  }
}

.table-dark {
  background-color: transparent;
}
</style>
